import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';

/**
 * What the server is doing, while it does it.
 *
 * Generation runs as a background job now (large flows take a few minutes), so this narrates the
 * job's own real progress, `progressNote` comes straight from the server, set at the moment each
 * stage actually starts, rather than guessed from an elapsed timer. Before the first poll answers
 * (the job is still QUEUED), a neutral opening line covers the gap.
 *
 * A generation running long enough to matter reads as broken rather than merely slow, so a single
 * reassurance line appears after 45 seconds and never goes away once shown, a message that comes
 * and goes reads as a glitch, not as information.
 */
const DEFAULT_NOTE = 'Reading what you asked for';
const STILL_WORKING_AFTER_MS = 45000;
const STILL_WORKING_MESSAGE = 'Still working, large flows can take a few minutes.';

const GenerationProgress = ({ startedAt, progressNote }) => {
  const [elapsed, setElapsed] = React.useState(0);

  React.useEffect(() => {
    const from = startedAt || Date.now();
    setElapsed(Date.now() - from);
    const timer = setInterval(() => setElapsed(Date.now() - from), 500);
    return () => clearInterval(timer);
  }, [startedAt]);

  return (
    <Box sx={{ py: 1.5 }} aria-live="polite">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {progressNote || DEFAULT_NOTE}
      </Typography>
      <LinearProgress sx={{ borderRadius: 1, height: 4 }} />
      {elapsed >= STILL_WORKING_AFTER_MS && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {STILL_WORKING_MESSAGE}
        </Typography>
      )}
    </Box>
  );
};

export default GenerationProgress;
